/**
 * Player-made clans: players create and run them (a leader, officers, members; joining open or by request), a clan
 * holds the zones of its current members (services/assault.ts: the clan's zones cannot be attacked by its members, and
 * the members share a tenth of each other's tribute, as in the original), its rating is the captures of its current
 * members, and it keeps a structured history. The 14 fixed clans of the original are gone (storage migration
 * drop-default-clans). The name rules: src/shared/clan-rules.ts; the numbers: config.clans (system.hello limits.clans).
 *
 * Records (services/clan-directory.ts): clans/<id>, clan_names/<nameKey>, clan_players/<playerId> (the ONE record of
 * membership), clan_requests/<clanId>~<playerId>, clan_history/<id>, meta/clan_seq. ClanDirectory is the in-memory
 * index every reader of membership uses; this service is the only writer of those documents and applies each batch
 * to the directory before it releases its locks.
 *
 * Invariants (verify(): the operator console's `clans.check`, and the tests after every scenario):
 *  1. a clan_players record with a clan points to an existing clan; memberCount / officerCount equal its records;
 *  2. every clan has exactly one leader, its leaderId;
 *  3. no clan is empty (the last member leaving disbands it);
 *  4. clan_names/<key> exists exactly for the existing clans' nameKeys;
 *  5. requests only to existing clans, only of players without a clan (joining any clan, a disband and a decision
 *     remove them in the same batch);
 *  6. officerCount <= maxOfficers, memberCount <= maxMembers, history <= historyKept events;
 *  7. a history names only existing accounts (or DELETED_PLAYER_ID): the account deletion anonymizes every history
 *     that names the player (a deletion that has not finished yet shows here until it does).
 *
 * LOCK ORDER (KeyedMutex keys; whoever takes more than one takes them in this order, so no cycle can form):
 *
 *     [profile:<p>] -> [zone:<n>, ascending] -> [auction] -> clanp:<p> (ascending player ids) -> clan:<c>
 *        -> clan-name:<key> -> clan-seq -> [auth:<p>]
 *
 *  - clanp:<playerId>: the player's membership - its move into a clan (create, join, an accepted request) and out of
 *    one (leave, kick, the operator's removal, the account deletion) - and its pending requests (request, cancel,
 *    decide; joining a clan removes them all).
 *  - clan:<clanId>: everything of the clan - its record, the roles of its members, the creation of its requests, its
 *    history. A member's record is written only under it, so under clan:<c> the directory's member list of c is exact
 *    (a disband and role changes need no clanp locks); a player's requests are written under its clanp lock.
 *  - clan-name:<key>: creating a clan with that name or renaming one to it (C.clanNames).
 *  - clan-seq: the clan id counter (meta/clan_seq).
 * Clan operations never take profile / zone / auction / auth locks. A zone capture (profile -> zone) appends to clan
 * histories under clan:<c> after its zone lock, one clan lock at a time (recordCapture); the account deletion
 * (services/account-deletion.ts: profile -> every zone -> auction -> clanp:<p> -> clan:<c> -> auth:<p>) reads the
 * player's clan before it locks, checks it under the locks and starts over when it changed; after its batch (and when
 * a deletion stopped there is finished later) it anonymizes the other clans' histories under one clan:<c> at a time,
 * holding no other lock (deletionHistories).
 *
 *   operation              locks                                   one batch writes
 *   create                 clanp:me -> clan-name -> clan-seq       clan, name, member, history, counter; its requests go
 *   join (open)            clanp:me -> clan:c                      member, clan (+1), history; the player's requests go
 *   request / cancel       clanp:me -> clan:c                      the request
 *   decide (accept)        clanp:player -> clan:c                  as join
 *   kick / leave           clanp:player -> clan:c                  member (no clan, leftAt), clan (-1), history
 *   role, transfer, update clan:c (-> clan-name when renamed)      members' roles / clan, history
 *   disband                clan:c                                  every member (no clan), clan, name, history, requests
 * Under its locks an operation reads everything it decides on again from storage (the acting member's role too: roles
 * change only under clan:<c>). Two officers of different clans accepting the same player take clanp:<player> one after
 * the other: the second finds the player in a clan (INVALID_STATE already_in_clan, its request removed). Every
 * mutation is `mutating` in the protocol (a retry under the same request id gets the stored reply), and repeating one
 * is harmless: a pending request is answered again, the same role changes nothing, leaving without a clan and
 * cancelling a missing request succeed.
 *
 * The history is informational: the events of a capture are added after the capture's own batch (a crash in between
 * loses them), and the account deletion anonymizes the player in the OTHER clans' histories after its batch (a
 * deletion interrupted there is finished by the next call, the sweep or the next start: its record in
 * C.accountDeletions).
 *
 * Reading is cheap whatever the number of clans and members: the ratings are kept by the directory (its members'
 * capture counts, updated as members come and go and after every stored capture: recordCapture reads the capturer's
 * account), the zone owners are kept here between changes (AssaultService.onOwnerChange, deletionStored), and
 * clan.list pages lists sorted once per change of the directory (`version`). A list call reads no document.
 */
import { BackendError } from '../../../shared/protocol/errors';
import {
  CLAN_DESCRIPTION_MAX,
  CLAN_NAME_MAX,
  CLAN_NAME_MIN,
  checkClanDescription,
  checkClanName,
  clanNameKey,
  clanTextKey,
  isClanEmblem,
  reservedWordIn,
} from '../../../shared/clan-rules';
import type { ClanLimits, ListPage } from '../../../shared/protocol/messages';
import {
  DELETED_PLAYER_ID,
  DELETED_PLAYER_NAME,
  type ClanEvent,
  type ClanInfo,
  type ClanJoinPolicy,
  type ClanJoinRequest,
  type ClanMember,
  type ClanMembershipReason,
  type ClanMineResult,
  type ClanRole,
  type ClanSummary,
  type PlayerId,
} from '../../../shared/protocol/model';
import { LIST_LIMITS } from '../../../shared/protocol/validators';
import type { BackendConfig } from '../config';
import { badCursor, cursorInt, decodeCursor, encodeCursor, keysetPage, pageLimit } from '../pagination';
import { type StorageBatchOp, deleteOp, putOp, skipDamaged } from '../storage';
import { withoutPlayerId } from './account-deletion';
import type { AssaultService, CaptureNotice, HeldZone } from './assault';
import type { AuthService } from './auth';
import {
  CLAN_SEQ_COLLECTION,
  CLAN_SEQ_ID,
  type ClanDirectory,
  type ClanEventRecord,
  type ClanHistoryRecord,
  type ClanNameRecord,
  type ClanPlayerRecord,
  type ClanRecord,
  type ClanRequestRecord,
  FIRST_CLAN_ID,
  clanRequestId,
  emptyClanPlayer,
} from './clan-directory';
import { C, type ServiceDeps } from './deps';
import type { ProfileService } from './profile';

/** Lock of a player's membership and requests (see the lock order in the header). */
export function clanPlayerLockKey(playerId: PlayerId): string {
  return `clanp:${playerId}`;
}

/** Lock of everything of one clan. */
export function clanLockKey(clanId: number): string {
  return `clan:${clanId}`;
}

/** Lock of a clan name (creating, renaming). */
export function clanNameLockKey(key: string): string {
  return `clan-name:${key}`;
}

/** Lock of the clan id counter. */
export const CLAN_SEQ_LOCK = 'clan-seq';

/** system.hello limits.clans of a configuration. */
export function clanLimits(config: BackendConfig): ClanLimits {
  const c = config.clans;
  return {
    nameMin: CLAN_NAME_MIN,
    nameMax: CLAN_NAME_MAX,
    descriptionMax: CLAN_DESCRIPTION_MAX,
    maxMembers: c.maxMembers,
    maxOfficers: c.maxOfficers,
    emblems: [...c.emblems],
    minLevelToCreate: c.minLevelToCreate,
    createCooldownMs: c.createCooldownMs,
    renameCooldownMs: c.renameCooldownMs,
    rejoinCooldownMs: c.rejoinCooldownMs,
    maxRequestsPerPlayer: c.maxRequestsPerPlayer,
    maxRequestsPerClan: c.maxRequestsPerClan,
  };
}

/** Order of the roles in member lists. */
const ROLE_RANK: Readonly<Record<ClanRole, number>> = { leader: 0, officer: 1, member: 2 };

/** A history event before it gets its number and time. */
type NewEvent = Omit<ClanEventRecord, 'seq' | 'at'>;

/** What the account deletion does to the clans (deletionOps -> deletionStored -> deletionEvents, deletionHistories). */
export interface ClanDeletion {
  /** The deleted player. */
  playerId: PlayerId;
  ops: StorageBatchOp[];
  /** The player's clan (null = none). */
  clanId: number | null;
  /** The clan had no other member and is gone. */
  disbanded: boolean;
  /** The member who became the leader in the deleted player's place. */
  successor: PlayerId | null;
  /** Clans whose pending requests changed (the player's requests went). */
  requestClans: number[];
}

/** Whether a JSON value holds the string `playerId` anywhere. */
function mentions(value: unknown, playerId: PlayerId): boolean {
  return JSON.stringify(value ?? null).includes(JSON.stringify(playerId));
}

export class ClanService {
  /** The clans sorted for clan.list, for one `version` of the directory (built when first asked for). */
  private sorted: { version: number; rating?: ClanRecord[]; created?: ClanRecord[] } = { version: -1 };
  /** Who holds each zone, while no owner changed (`ownersGen`): the clans' zone counts. */
  private owners: { gen: number; owners: ReadonlyMap<number, PlayerId> } | null = null;
  private ownersGen = 0;

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly profiles: ProfileService,
    readonly directory: ClanDirectory,
    private readonly assault: AssaultService,
  ) {
    assault.onOwnerChange(() => this.zonesChanged());
    assault.onCapture((c) => this.recordCapture(c));
  }

  private get cfg(): BackendConfig['clans'] {
    return this.d.config.clans;
  }

  /** The rules clients see (system.hello limits.clans). */
  limits(): ClanLimits {
    return clanLimits(this.d.config);
  }

  // ------------------------------------------------------------------------------------------------ plumbing

  private locked<T>(keys: readonly string[], fn: () => Promise<T>): Promise<T> {
    return this.d.mutex.runAll(keys, fn);
  }

  private readClan(clanId: number): Promise<ClanRecord | null> {
    return this.d.storage.get<ClanRecord>(C.clans, String(clanId));
  }

  private async requireClan(clanId: number): Promise<ClanRecord> {
    const rec = await this.readClan(clanId);
    if (!rec) throw new BackendError('NOT_FOUND', `Clan ${clanId} does not exist`, { reason: 'clan_not_found', clanId });
    return rec;
  }

  private async readPlayer(playerId: PlayerId): Promise<ClanPlayerRecord> {
    return (await this.d.storage.get<ClanPlayerRecord>(C.clanPlayers, playerId)) ?? emptyClanPlayer(playerId);
  }

  /**
   * Under the player's clanp lock: the account still exists. The account deletion removes the player's clan records
   * under the same lock, so a request of the player that was already under way cannot bring any of them back.
   */
  private async assertAccount(playerId: PlayerId): Promise<void> {
    if (!(await this.auth.getAccount(playerId))) throw new BackendError('NOT_FOUND', 'The account no longer exists', { reason: 'account_not_found' });
  }

  /** Stores one mutation's writes in one batch and applies them to the directory (under the mutation's locks). */
  private async commit(ops: StorageBatchOp[]): Promise<void> {
    if (ops.length === 0) return;
    await this.d.storage.batch(ops);
    this.directory.apply(ops);
  }

  /** The history document with `events` appended (numbered, the oldest beyond historyKept dropped). Under clan:<c>. */
  private async historyOp(clanId: number, events: readonly NewEvent[], at: number, base?: ClanHistoryRecord): Promise<StorageBatchOp> {
    const h = base ?? (await this.d.storage.get<ClanHistoryRecord>(C.clanHistory, String(clanId))) ?? { clanId, seq: 0, events: [] };
    for (const e of events) {
      h.seq += 1;
      h.events.push({ seq: h.seq, at, ...e });
    }
    if (h.events.length > this.cfg.historyKept) h.events.splice(0, h.events.length - this.cfg.historyKept);
    return putOp(C.clanHistory, String(clanId), h);
  }

  /** The clan record after a change. */
  private bump(rec: ClanRecord, now: number, change: Partial<ClanRecord>): ClanRecord {
    return { ...rec, ...change, updatedAt: now, version: rec.version + 1 };
  }

  /** The player's record after it left a clan (`cooldown`: it left or was removed, so the rejoin cooldown starts). */
  private leftRecord(p: ClanPlayerRecord, now: number, cooldown: boolean): ClanPlayerRecord {
    return { ...p, clanId: null, role: null, joinedAt: null, leftAt: cooldown ? now : p.leftAt };
  }

  private async levelOf(playerId: PlayerId): Promise<number> {
    return (await this.profiles.getRecord(playerId))?.profile.summary.level ?? 0;
  }

  /** The level shown of another player (a member, a requester): a damaged profile shows 0 (logged), never fails the view. */
  private async shownLevel(playerId: PlayerId): Promise<number> {
    return (await skipDamaged(() => this.profiles.getRecord(playerId), this.d.logger, { playerId }))?.profile.summary.level ?? 0;
  }

  /**
   * The current display names and capture counts of players, from their accounts (a missing account, a deleted player:
   * DELETED_PLAYER_NAME and 0; so is a damaged account, logged, so that it does not fail the view of every player).
   * One read per player.
   */
  private async people(ids: Iterable<PlayerId | null | undefined>): Promise<Map<PlayerId, { name: string; captures: number }>> {
    const out = new Map<PlayerId, { name: string; captures: number }>();
    for (const id of ids) {
      if (!id || out.has(id)) continue;
      const a = id === DELETED_PLAYER_ID ? null : await skipDamaged(() => this.auth.getAccount(id), this.d.logger, { playerId: id });
      out.set(id, { name: a?.displayName ?? DELETED_PLAYER_NAME, captures: Number(a?.captures) || 0 });
    }
    return out;
  }

  private async names(ids: Iterable<PlayerId | null | undefined>): Promise<Map<PlayerId, string>> {
    return new Map([...(await this.people(ids))].map(([id, p]) => [id, p.name]));
  }

  /** A zone's owner changed (a capture, a reset, an account deletion): the zone counts are read again. */
  private zonesChanged(): void {
    this.ownersGen++;
    this.owners = null;
  }

  /** Who holds each zone (kept until an owner changes; a read under way while one changes is not kept). */
  private async zoneOwners(): Promise<ReadonlyMap<number, PlayerId>> {
    if (this.owners && this.owners.gen === this.ownersGen) return this.owners.owners;
    const gen = this.ownersGen;
    const owners = await this.assault.zoneOwners();
    if (gen === this.ownersGen) this.owners = { gen, owners };
    return owners;
  }

  /**
   * The number of zones each clan holds: the zones of its current members. Synchronous, so that the caller counts with
   * the membership it shows (called after its last await).
   */
  private zoneCounts(owners: ReadonlyMap<number, PlayerId>): Map<number, number> {
    const counts = new Map<number, number>();
    for (const owner of owners.values()) {
      const c = this.directory.clanIdOf(owner);
      if (c !== null) counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return counts;
  }

  /**
   * Every clan by rating (the highest first, then the lowest id) or the newest first: sorted once per `version` of the
   * directory. The rating of a clan is its members' captures (as in the original, all of their captures).
   */
  private sortedClans(sort: 'rating' | 'new'): readonly ClanRecord[] {
    const version = this.directory.version;
    if (this.sorted.version !== version) this.sorted = { version };
    const s = this.sorted;
    if (sort === 'new') return (s.created ??= this.directory.allClans().sort((a, b) => b.createdAt - a.createdAt || b.clanId - a.clanId));
    const rating = (r: ClanRecord) => this.directory.ratingOf(r.clanId);
    return (s.rating ??= this.directory.allClans().sort((a, b) => rating(b) - rating(a) || a.clanId - b.clanId));
  }

  /** The acting member's record; refused unless it is the clan's leader (`need` 'leader') or leader or officer. */
  private async actor(playerId: PlayerId, clanId: number, need: 'leader' | 'officer'): Promise<ClanPlayerRecord> {
    const a = await this.readPlayer(playerId);
    const role = a.clanId === clanId ? a.role : null;
    if (need === 'leader' ? role !== 'leader' : role !== 'leader' && role !== 'officer') {
      throw new BackendError('FORBIDDEN', need === 'leader' ? 'Only the leader of the clan may do this' : 'Only the leader or an officer of the clan may do this', {
        reason: 'clan_rank_too_low',
        role,
        need,
      });
    }
    return a;
  }

  private checkName(raw: string, reserved = true): { name: string; key: string } {
    const n = checkClanName(raw);
    if (!n.ok) throw new BackendError('VALIDATION_FAILED', `The clan name is not acceptable (${n.reason})`, { reason: n.reason, field: 'name' });
    if (reserved && reservedWordIn(n.name, this.cfg.denylist) !== null) {
      throw new BackendError('VALIDATION_FAILED', 'The clan name contains a reserved word', { reason: 'clan_name_denied', field: 'name' });
    }
    return { name: n.name, key: n.key };
  }

  private checkEmblem(emblem: number): void {
    if (!isClanEmblem(emblem, this.cfg.emblems)) throw new BackendError('VALIDATION_FAILED', `Emblem ${emblem} is not one of the clan emblems`, { reason: 'emblem_invalid', field: 'emblem', emblems: [...this.cfg.emblems] });
  }

  private checkDescription(raw: string | undefined): string {
    const c = checkClanDescription(raw);
    if (!c.ok) throw new BackendError('VALIDATION_FAILED', `The description is not acceptable (${c.reason})`, { reason: c.reason, field: 'description' });
    if (c.description !== '' && reservedWordIn(c.description, this.cfg.denylist) !== null) {
      throw new BackendError('VALIDATION_FAILED', 'The description contains a reserved word', { reason: 'description_denied', field: 'description' });
    }
    return c.description;
  }

  private assertRejoin(p: ClanPlayerRecord, now: number): void {
    const until = p.leftAt === null ? 0 : p.leftAt + this.cfg.rejoinCooldownMs;
    if (until > now) {
      throw new BackendError('RATE_LIMITED', 'A player who left a clan joins another one only after the cooldown', { reason: 'rejoin_cooldown', retryAfterMs: until - now });
    }
  }

  private assertRoom(rec: ClanRecord): void {
    if (rec.memberCount >= this.cfg.maxMembers) throw new BackendError('INVALID_STATE', 'The clan is full', { reason: 'clan_full', clanId: rec.clanId, maxMembers: this.cfg.maxMembers });
  }

  // ------------------------------------------------------------------------------------------------ events

  private sendMembership(playerId: PlayerId, clanId: number | null, role: ClanRole | null, reason: ClanMembershipReason): void {
    this.d.hub.send([playerId], 'clan.membership', { clanId, role, reason });
  }

  /** clan.updated with the new card to every member. */
  private async sendUpdated(clanId: number): Promise<void> {
    const rec = this.directory.clan(clanId);
    if (!rec) return;
    this.d.hub.send(this.directory.membersOf(clanId), 'clan.updated', { clan: await this.infoOf(rec) });
  }

  /** clan.requestsChanged to the leader and the officers of each clan. */
  private sendRequests(clanIds: Iterable<number>): void {
    for (const clanId of new Set(clanIds)) {
      if (!this.directory.clan(clanId)) continue;
      const staff = this.directory.membersOf(clanId).filter((p) => {
        const role = this.directory.roleOf(p);
        return role === 'leader' || role === 'officer';
      });
      this.d.hub.send(staff, 'clan.requestsChanged', { clanId, pending: this.directory.requestsOfClan(clanId).length });
    }
  }

  // ------------------------------------------------------------------------------------------------ views

  private summaryOf(rec: ClanRecord, zoneCount: number, rating: number): ClanSummary {
    return {
      clanId: rec.clanId,
      name: rec.name,
      rating,
      memberCount: rec.memberCount,
      emblem: rec.emblem,
      joinPolicy: rec.joinPolicy,
      zoneCount,
      maxMembers: this.cfg.maxMembers,
    };
  }

  private async infoOf(rec: ClanRecord, held?: HeldZone[]): Promise<ClanInfo> {
    const all = held ?? (await this.assault.heldZones());
    const leaderName = (await this.names([rec.leaderId])).get(rec.leaderId) ?? DELETED_PLAYER_NAME;
    await this.directory.settleCaptures();
    // The members, the zones and the rating of one state of the directory (nothing is awaited in between).
    const members = new Set(this.directory.membersOf(rec.clanId));
    const zones = all.filter((z) => members.has(z.ownerId)).map((z) => ({ ...z }));
    return { ...this.summaryOf(rec, zones.length, this.directory.ratingOf(rec.clanId)), description: rec.description, leaderId: rec.leaderId, leaderName, createdAt: rec.createdAt, zones };
  }

  /** The card of a clan, or null when it does not exist. */
  async info(clanId: number): Promise<ClanInfo | null> {
    await this.directory.ready();
    const rec = this.directory.clan(clanId);
    return rec ? this.infoOf(rec) : null;
  }

  /** clan.get. */
  async get(clanId: number): Promise<ClanInfo> {
    const info = await this.info(clanId);
    if (!info) throw new BackendError('NOT_FOUND', `Clan ${clanId} does not exist`, { reason: 'clan_not_found', clanId });
    return info;
  }

  private async requestView(r: ClanRequestRecord): Promise<ClanJoinRequest> {
    const name = (await this.names([r.playerId])).get(r.playerId) ?? DELETED_PLAYER_NAME;
    return { clanId: r.clanId, clanName: this.directory.clan(r.clanId)?.name ?? '', playerId: r.playerId, displayName: name, level: await this.shownLevel(r.playerId), at: r.at };
  }

  private eventView(e: ClanEventRecord, names: ReadonlyMap<PlayerId, string>): ClanEvent {
    const name = (id: PlayerId | null | undefined) => (id ? (names.get(id) ?? DELETED_PLAYER_NAME) : null);
    const out: ClanEvent = { seq: e.seq, at: e.at, type: e.type, actorId: e.actorId, actorName: name(e.actorId), targetId: e.targetId, targetName: name(e.targetId) };
    if (e.zone !== undefined) out.zone = e.zone;
    if (e.stage !== undefined) out.stage = e.stage;
    if (e.zones !== undefined) out.zones = [...e.zones];
    if (e.role !== undefined) out.role = e.role;
    if (e.from !== undefined) out.from = e.from;
    if (e.to !== undefined) out.to = e.to;
    if (e.emblem !== undefined) out.emblem = e.emblem;
    if (e.joinPolicy !== undefined) out.joinPolicy = e.joinPolicy;
    if (e.description !== undefined) out.description = e.description;
    if (e.via !== undefined) out.via = e.via;
    if (e.byId !== undefined) {
      out.byId = e.byId;
      out.byName = name(e.byId);
    }
    if (e.byClanId !== undefined) {
      out.byClanId = e.byClanId;
      out.byClanName = e.byClanId === null ? null : (this.directory.clan(e.byClanId)?.name ?? e.byClanName ?? null);
    }
    if (e.action !== undefined) out.action = e.action;
    if (e.reason !== undefined) out.reason = e.reason;
    return out;
  }

  /** The zones a player holds now (its zones leave the clan with it). */
  private async zonesOf(playerId: PlayerId): Promise<number[]> {
    return (await this.assault.heldZones()).filter((z) => z.ownerId === playerId).map((z) => z.zone);
  }

  // ------------------------------------------------------------------------------------------------ reads

  /**
   * clan.list: one page by rating (the highest first; cursor `cl.<rating>.<clanId>`) or the newest first (`cn.<createdAt>.
   * <clanId>`), optionally only the names that contain `query` (compared by clanTextKey).
   */
  async list(opts: { sort?: 'rating' | 'new'; query?: string; limit?: number; cursor?: string } = {}): Promise<ListPage<ClanSummary>> {
    const limit = pageLimit(opts.limit, LIST_LIMITS['clan.list'].default, LIST_LIMITS['clan.list'].max);
    const byAge = opts.sort === 'new';
    const cursor = opts.cursor !== undefined ? decodeCursor(opts.cursor, byAge ? 'cn' : 'cl', 2).map((p) => cursorInt(p)) : null;
    const owners = await this.zoneOwners();
    await this.directory.settleCaptures();
    // From here on nothing is awaited: the order, the ratings, the zone counts and the page come from one state of the
    // directory.
    const zoneCounts = this.zoneCounts(owners);
    const rating = (r: ClanRecord) => this.directory.ratingOf(r.clanId);
    let recs = this.sortedClans(byAge ? 'new' : 'rating');
    if (opts.query !== undefined) {
      const q = clanTextKey(opts.query);
      recs = q === '' ? [] : recs.filter((c) => c.nameKey.includes(q));
    }
    let after: ((r: ClanRecord) => boolean) | null = null;
    let cursorOf: (r: ClanRecord) => string;
    if (byAge) {
      if (cursor) {
        const [t, id] = cursor;
        after = (r) => r.createdAt < t || (r.createdAt === t && r.clanId < id);
      }
      cursorOf = (r) => encodeCursor('cn', [r.createdAt, r.clanId]);
    } else {
      if (cursor) {
        const [at, id] = cursor;
        after = (r) => rating(r) < at || (rating(r) === at && r.clanId > id);
      }
      cursorOf = (r) => encodeCursor('cl', [rating(r), r.clanId]);
    }
    const page = keysetPage(recs, limit, after, cursorOf);
    return { items: page.items.map((rec) => this.summaryOf(rec, zoneCounts.get(rec.clanId) ?? 0, rating(rec))), next: page.next };
  }

  /** Every member: the leader, the officers, the members, each group by captures (most first), then by id. */
  async members(clanId: number): Promise<ClanMember[]> {
    await this.directory.ready();
    if (!this.directory.clan(clanId)) throw new BackendError('NOT_FOUND', `Clan ${clanId} does not exist`, { reason: 'clan_not_found', clanId });
    const ids = this.directory.membersOf(clanId);
    const people = await this.people(ids);
    const out: ClanMember[] = [];
    for (const id of ids) {
      const p = this.directory.player(id);
      const person = people.get(id);
      out.push({ playerId: id, displayName: person?.name ?? DELETED_PLAYER_NAME, level: await this.shownLevel(id), captures: person?.captures ?? 0, role: p?.role ?? 'member', joinedAt: p?.joinedAt ?? 0 });
    }
    return out.sort((x, y) => ROLE_RANK[x.role] - ROLE_RANK[y.role] || y.captures - x.captures || (x.playerId < y.playerId ? -1 : x.playerId > y.playerId ? 1 : 0));
  }

  /**
   * One page of `members` (cursor `cm.<clanId>.<role rank>.<captures>.<playerId>`); a cursor of another clan or a
   * malformed one is BAD_REQUEST 'bad_cursor'.
   */
  async membersPage(clanId: number, opts: { limit?: number; cursor?: string } = {}): Promise<ListPage<ClanMember>> {
    const limit = pageLimit(opts.limit, LIST_LIMITS['clan.members'].default, LIST_LIMITS['clan.members'].max);
    let after: ((m: ClanMember) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [clan, rankPart, caps, id] = decodeCursor(opts.cursor, 'cm', 4);
      if (cursorInt(clan) !== clanId) throw badCursor('clan member');
      const rank = cursorInt(rankPart);
      const captures = cursorInt(caps);
      after = (m) => ROLE_RANK[m.role] > rank || (ROLE_RANK[m.role] === rank && (m.captures < captures || (m.captures === captures && m.playerId > id)));
    }
    return keysetPage(await this.members(clanId), limit, after, (m) => encodeCursor('cm', [clanId, ROLE_RANK[m.role], m.captures, m.playerId]));
  }

  /** clan.mine. */
  async mine(playerId: PlayerId): Promise<ClanMineResult> {
    await this.directory.ready();
    const now = this.d.clock.now();
    const p = this.directory.player(playerId) ?? emptyClanPlayer(playerId);
    const rec = p.clanId !== null ? this.directory.clan(p.clanId) : null;
    const role = rec ? p.role : null;
    const requests: ClanJoinRequest[] = [];
    for (const r of this.directory.requestsOfPlayer(playerId)) requests.push(await this.requestView(r));
    const staff = role === 'leader' || role === 'officer';
    const rejoin = p.leftAt === null ? 0 : p.leftAt + this.cfg.rejoinCooldownMs;
    return {
      clan: rec ? await this.infoOf(rec) : null,
      role,
      requests,
      pendingRequests: rec && staff ? this.directory.requestsOfClan(rec.clanId).length : 0,
      rejoinAt: rejoin > now ? rejoin : null,
      canCreate: await this.creationCheck(playerId, p, now),
    };
  }

  /** Whether clan.create would pass now (the checks of `create` that need no input). */
  private async creationCheck(playerId: PlayerId, p: ClanPlayerRecord, now: number): Promise<ClanMineResult['canCreate']> {
    const a = await this.auth.getAccount(playerId);
    if (!a || a.username === null) return { ok: false, reason: 'guest_not_allowed' };
    if (p.clanId !== null) return { ok: false, reason: 'already_in_clan' };
    const until = p.createdClanAt === null ? 0 : p.createdClanAt + this.cfg.createCooldownMs;
    if (until > now) return { ok: false, reason: 'create_cooldown', at: until };
    if ((await this.levelOf(playerId)) < this.cfg.minLevelToCreate) return { ok: false, reason: 'clan_level_too_low' };
    return { ok: true };
  }

  /** clan.requests: the clan's pending requests, oldest first (leader and officers); cursor `cr.<clanId>.<at>.<playerId>`. */
  async requestsPage(playerId: PlayerId, clanId: number, opts: { limit?: number; cursor?: string } = {}): Promise<ListPage<ClanJoinRequest>> {
    await this.directory.ready();
    if (!this.directory.clan(clanId)) throw new BackendError('NOT_FOUND', `Clan ${clanId} does not exist`, { reason: 'clan_not_found', clanId });
    const role = this.directory.clanIdOf(playerId) === clanId ? this.directory.roleOf(playerId) : null;
    if (role !== 'leader' && role !== 'officer') throw new BackendError('FORBIDDEN', 'Only the leader or an officer of the clan sees its requests', { reason: 'clan_rank_too_low', role, need: 'officer' });
    const limit = pageLimit(opts.limit, LIST_LIMITS['clan.requests'].default, LIST_LIMITS['clan.requests'].max);
    let after: ((r: ClanRequestRecord) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [clan, atPart, id] = decodeCursor(opts.cursor, 'cr', 3);
      if (cursorInt(clan) !== clanId) throw badCursor('clan request');
      const at = cursorInt(atPart);
      after = (r) => r.at > at || (r.at === at && r.playerId > id);
    }
    const page = keysetPage(this.directory.requestsOfClan(clanId), limit, after, (r) => encodeCursor('cr', [clanId, r.at, r.playerId]));
    const items: ClanJoinRequest[] = [];
    for (const r of page.items) items.push(await this.requestView(r));
    return { items, next: page.next };
  }

  /** clan.history: newest first; cursor `ch.<clanId>.<seq>`. */
  async historyPage(clanId: number, opts: { limit?: number; cursor?: string } = {}): Promise<ListPage<ClanEvent>> {
    await this.directory.ready();
    if (!this.directory.clan(clanId)) throw new BackendError('NOT_FOUND', `Clan ${clanId} does not exist`, { reason: 'clan_not_found', clanId });
    const limit = pageLimit(opts.limit, LIST_LIMITS['clan.history'].default, LIST_LIMITS['clan.history'].max);
    let after: ((e: ClanEventRecord) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [clan, seqPart] = decodeCursor(opts.cursor, 'ch', 2);
      if (cursorInt(clan) !== clanId) throw badCursor('clan history');
      const seq = cursorInt(seqPart);
      after = (e) => e.seq < seq;
    }
    const h = await this.d.storage.get<ClanHistoryRecord>(C.clanHistory, String(clanId));
    const events = [...(h?.events ?? [])].sort((a, b) => b.seq - a.seq);
    const page = keysetPage(events, limit, after, (e) => encodeCursor('ch', [clanId, e.seq]));
    const names = await this.names(page.items.flatMap((e) => [e.actorId, e.targetId, e.byId]));
    return { items: page.items.map((e) => this.eventView(e, names)), next: page.next };
  }

  // ------------------------------------------------------------------------------------------------ player mutations

  /** clan.create (see the header: locks clanp:me -> clan-name -> clan-seq). */
  async create(playerId: PlayerId, p: { name: string; emblem: number; joinPolicy?: ClanJoinPolicy; description?: string }): Promise<{ clan: ClanInfo }> {
    const name = this.checkName(p.name);
    this.checkEmblem(p.emblem);
    const description = this.checkDescription(p.description);
    const account = await this.auth.requireAccount(playerId);
    if (account.username === null) throw new BackendError('FORBIDDEN', 'A guest account cannot create a clan: sign up first', { reason: 'guest_not_allowed' });
    const level = await this.levelOf(playerId);
    if (level < this.cfg.minLevelToCreate) {
      throw new BackendError('FORBIDDEN', `Creating a clan needs level ${this.cfg.minLevelToCreate}`, { reason: 'clan_level_too_low', minLevel: this.cfg.minLevelToCreate, level });
    }
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(playerId), clanNameLockKey(name.key), CLAN_SEQ_LOCK], async () => {
      const now = this.d.clock.now();
      await this.assertAccount(playerId);
      const me = await this.readPlayer(playerId);
      if (me.clanId !== null) throw new BackendError('INVALID_STATE', 'Leave the current clan first', { reason: 'already_in_clan', clanId: me.clanId });
      const until = me.createdClanAt === null ? 0 : me.createdClanAt + this.cfg.createCooldownMs;
      if (until > now) throw new BackendError('RATE_LIMITED', 'A player creates one clan per cooldown period', { reason: 'create_cooldown', retryAfterMs: until - now });
      if (await this.d.storage.get(C.clanNames, name.key)) throw new BackendError('CONFLICT', 'Another clan has this name', { reason: 'clan_name_taken', field: 'name' });
      let clanId = Math.max(FIRST_CLAN_ID, (await this.d.storage.get<{ next: number }>(CLAN_SEQ_COLLECTION, CLAN_SEQ_ID))?.next ?? FIRST_CLAN_ID);
      while (await this.readClan(clanId)) clanId++; // a counter that fell behind (a restored backup) skips the used ids
      const rec: ClanRecord = {
        clanId,
        name: name.name,
        nameKey: name.key,
        emblem: p.emblem,
        joinPolicy: p.joinPolicy ?? 'request',
        description,
        leaderId: playerId,
        memberCount: 1,
        officerCount: 0,
        createdAt: now,
        updatedAt: now,
        renamedAt: 0,
        version: 1,
      };
      const dropped = this.directory.requestsOfPlayer(playerId);
      await this.commit([
        putOp(C.clans, String(clanId), rec),
        putOp(C.clanNames, name.key, { clanId, name: name.name, key: name.key, at: now } satisfies ClanNameRecord),
        putOp(C.clanPlayers, playerId, { ...me, clanId, role: 'leader', joinedAt: now, createdClanAt: now } satisfies ClanPlayerRecord),
        putOp(C.clanHistory, String(clanId), { clanId, seq: 1, events: [{ seq: 1, at: now, type: 'created', actorId: playerId, targetId: null }] } satisfies ClanHistoryRecord),
        ...dropped.map((r) => deleteOp(C.clanRequests, clanRequestId(r.clanId, playerId))),
        putOp(CLAN_SEQ_COLLECTION, CLAN_SEQ_ID, { next: clanId + 1 }),
      ]);
      this.d.logger.info('clan created', { clanId, playerId });
      this.sendMembership(playerId, clanId, 'leader', 'created');
      await this.sendUpdated(clanId);
      this.sendRequests(dropped.map((r) => r.clanId));
      return { clan: await this.infoOf(this.directory.clan(clanId)!) };
    });
  }

  /** clan.update (clan:c, -> clan-name:<new key> when renamed). */
  async update(playerId: PlayerId, clanId: number, p: { name?: string; emblem?: number; joinPolicy?: ClanJoinPolicy; description?: string }): Promise<{ clan: ClanInfo }> {
    const name = p.name !== undefined ? this.checkName(p.name) : null;
    if (p.emblem !== undefined) this.checkEmblem(p.emblem);
    const description = p.description !== undefined ? this.checkDescription(p.description) : undefined;
    await this.directory.ready();
    return this.locked([clanLockKey(clanId), ...(name ? [clanNameLockKey(name.key)] : [])], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      await this.actor(playerId, clanId, name !== null || p.emblem !== undefined || p.joinPolicy !== undefined ? 'leader' : 'officer');
      const change: Partial<ClanRecord> = {};
      const ops: StorageBatchOp[] = [];
      const events: NewEvent[] = [];
      if (name && name.name !== rec.name) {
        const until = rec.renamedAt === 0 ? 0 : rec.renamedAt + this.cfg.renameCooldownMs;
        if (until > now) throw new BackendError('RATE_LIMITED', 'The clan was renamed recently', { reason: 'rename_cooldown', retryAfterMs: until - now });
        if (name.key !== rec.nameKey) {
          if (await this.d.storage.get(C.clanNames, name.key)) throw new BackendError('CONFLICT', 'Another clan has this name', { reason: 'clan_name_taken', field: 'name' });
          ops.push(deleteOp(C.clanNames, rec.nameKey));
        }
        ops.push(putOp(C.clanNames, name.key, { clanId, name: name.name, key: name.key, at: now } satisfies ClanNameRecord));
        Object.assign(change, { name: name.name, nameKey: name.key, renamedAt: now });
        events.push({ type: 'renamed', actorId: playerId, targetId: null, from: rec.name, to: name.name });
      }
      const settings: NewEvent = { type: 'settings', actorId: playerId, targetId: null };
      if (p.emblem !== undefined && p.emblem !== rec.emblem) change.emblem = settings.emblem = p.emblem;
      if (p.joinPolicy !== undefined && p.joinPolicy !== rec.joinPolicy) change.joinPolicy = settings.joinPolicy = p.joinPolicy;
      if (description !== undefined && description !== rec.description) {
        change.description = description;
        settings.description = true;
      }
      if (settings.emblem !== undefined || settings.joinPolicy !== undefined || settings.description) events.push(settings);
      if (events.length === 0) return { clan: await this.infoOf(rec) };
      ops.push(putOp(C.clans, String(clanId), this.bump(rec, now, change)), await this.historyOp(clanId, events, now));
      await this.commit(ops);
      await this.sendUpdated(clanId);
      return { clan: await this.infoOf(this.directory.clan(clanId)!) };
    });
  }

  /** Takes a player without a clan into it (join, an accepted request): under clanp:<player> -> clan:<c>. */
  private async admit(rec: ClanRecord, p: ClanPlayerRecord, now: number, via: ClanJoinPolicy, by: PlayerId | null, reason: ClanMembershipReason): Promise<void> {
    const dropped = this.directory.requestsOfPlayer(p.playerId);
    await this.commit([
      putOp(C.clanPlayers, p.playerId, { ...p, clanId: rec.clanId, role: 'member', joinedAt: now } satisfies ClanPlayerRecord),
      putOp(C.clans, String(rec.clanId), this.bump(rec, now, { memberCount: rec.memberCount + 1 })),
      await this.historyOp(rec.clanId, [{ type: 'joined', actorId: p.playerId, targetId: null, via, ...(by !== null ? { byId: by } : {}) }], now),
      ...dropped.map((r) => deleteOp(C.clanRequests, clanRequestId(r.clanId, p.playerId))),
    ]);
    this.sendMembership(p.playerId, rec.clanId, 'member', reason);
    await this.sendUpdated(rec.clanId);
    this.sendRequests(dropped.map((r) => r.clanId));
  }

  /** clan.join (clanp:me -> clan:c). */
  async join(playerId: PlayerId, clanId: number): Promise<{ clanId: number; role: ClanRole }> {
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(playerId), clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      await this.assertAccount(playerId);
      const me = await this.readPlayer(playerId);
      if (me.clanId === clanId) return { clanId, role: me.role ?? 'member' };
      if (me.clanId !== null) throw new BackendError('INVALID_STATE', 'Leave the current clan first', { reason: 'already_in_clan', clanId: me.clanId });
      if (rec.joinPolicy !== 'open') throw new BackendError('FORBIDDEN', 'This clan takes new members by request', { reason: 'clan_request_required', clanId });
      this.assertRejoin(me, now);
      this.assertRoom(rec);
      await this.admit(rec, me, now, 'open', null, 'joined');
      return { clanId, role: 'member' };
    });
  }

  /** clan.request (clanp:me -> clan:c). */
  async request(playerId: PlayerId, clanId: number): Promise<{ request: ClanJoinRequest }> {
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(playerId), clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      await this.assertAccount(playerId);
      const me = await this.readPlayer(playerId);
      if (me.clanId !== null) throw new BackendError('INVALID_STATE', 'Leave the current clan first', { reason: 'already_in_clan', clanId: me.clanId });
      const existing = await this.d.storage.get<ClanRequestRecord>(C.clanRequests, clanRequestId(clanId, playerId));
      if (existing) return { request: await this.requestView(existing) };
      if (rec.joinPolicy === 'open') throw new BackendError('INVALID_STATE', 'This clan is open: join it directly', { reason: 'clan_open', clanId });
      this.assertRejoin(me, now);
      this.assertRoom(rec);
      if (this.directory.requestsOfPlayer(playerId).length >= this.cfg.maxRequestsPerPlayer) {
        throw new BackendError('INVALID_STATE', 'Too many pending requests', { reason: 'request_limit', limit: this.cfg.maxRequestsPerPlayer });
      }
      if (this.directory.requestsOfClan(clanId).length >= this.cfg.maxRequestsPerClan) {
        throw new BackendError('INVALID_STATE', 'The clan has too many pending requests', { reason: 'clan_requests_full', limit: this.cfg.maxRequestsPerClan });
      }
      const r: ClanRequestRecord = { clanId, playerId, at: now };
      await this.commit([putOp(C.clanRequests, clanRequestId(clanId, playerId), r)]);
      this.sendRequests([clanId]);
      return { request: await this.requestView(r) };
    });
  }

  /** clan.cancelRequest (clanp:me -> clan:c). */
  async cancelRequest(playerId: PlayerId, clanId: number): Promise<Record<string, never>> {
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(playerId), clanLockKey(clanId)], async () => {
      const id = clanRequestId(clanId, playerId);
      if (!(await this.d.storage.get(C.clanRequests, id))) return {};
      await this.commit([deleteOp(C.clanRequests, id)]);
      this.sendRequests([clanId]);
      return {};
    });
  }

  /** clan.decide (clanp:<requester> -> clan:c). */
  async decide(playerId: PlayerId, clanId: number, requester: PlayerId, accept: boolean): Promise<{ clan?: ClanInfo }> {
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(requester), clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      await this.actor(playerId, clanId, 'officer');
      const id = clanRequestId(clanId, requester);
      const pending = (await this.d.storage.get(C.clanRequests, id)) !== null;
      const p = await this.readPlayer(requester);
      if (accept && p.clanId !== null) {
        // Accepted elsewhere meanwhile (joining a clan removes the player's requests; one left over goes now).
        if (pending) {
          await this.commit([deleteOp(C.clanRequests, id)]);
          this.sendRequests([clanId]);
        }
        throw new BackendError('INVALID_STATE', 'The player is in a clan by now', { reason: 'already_in_clan', clanId: p.clanId, playerId: requester });
      }
      if (!pending) throw new BackendError('NOT_FOUND', 'No such pending request', { reason: 'request_not_found', playerId: requester });
      if (!accept) {
        await this.commit([deleteOp(C.clanRequests, id)]);
        this.sendRequests([clanId]);
        this.sendMembership(requester, p.clanId, p.clanId !== null ? p.role : null, 'declined');
        return {};
      }
      this.assertRoom(rec);
      await this.admit(rec, p, now, 'request', playerId, 'accepted');
      return { clan: await this.infoOf(this.directory.clan(clanId)!) };
    });
  }

  /** clan.kick (clanp:<member> -> clan:c). */
  async kick(playerId: PlayerId, clanId: number, target: PlayerId): Promise<Record<string, never>> {
    if (target === playerId) throw new BackendError('VALIDATION_FAILED', 'Leave the clan with clan.leave', { reason: 'cannot_target_self' });
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(target), clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      const actor = await this.actor(playerId, clanId, 'officer');
      const t = await this.readPlayer(target);
      if (t.clanId !== clanId) throw new BackendError('NOT_FOUND', 'The player is not a member of this clan', { reason: 'not_a_member', playerId: target });
      if (t.role === 'leader') throw new BackendError('FORBIDDEN', 'The leader cannot be kicked', { reason: 'target_is_leader' });
      if (t.role === 'officer' && actor.role !== 'leader') throw new BackendError('FORBIDDEN', 'Only the leader removes an officer', { reason: 'clan_rank_too_low', role: actor.role, need: 'leader' });
      const zones = await this.zonesOf(target);
      await this.commit([
        putOp(C.clanPlayers, target, this.leftRecord(t, now, true)),
        putOp(C.clans, String(clanId), this.bump(rec, now, { memberCount: rec.memberCount - 1, officerCount: rec.officerCount - (t.role === 'officer' ? 1 : 0) })),
        await this.historyOp(clanId, [{ type: 'kicked', actorId: playerId, targetId: target, zones }], now),
      ]);
      this.sendMembership(target, null, null, 'kicked');
      await this.sendUpdated(clanId);
      return {};
    });
  }

  /** clan.setRole (clan:c). */
  async setRole(playerId: PlayerId, clanId: number, target: PlayerId, role: 'officer' | 'member'): Promise<Record<string, never>> {
    if (target === playerId) throw new BackendError('VALIDATION_FAILED', 'Not on yourself', { reason: 'cannot_target_self' });
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      await this.actor(playerId, clanId, 'leader');
      const t = await this.readPlayer(target);
      if (t.clanId !== clanId) throw new BackendError('NOT_FOUND', 'The player is not a member of this clan', { reason: 'not_a_member', playerId: target });
      if (t.role === role) return {};
      if (role === 'officer' && rec.officerCount >= this.cfg.maxOfficers) {
        throw new BackendError('INVALID_STATE', 'The clan has the most officers it may have', { reason: 'officer_limit', limit: this.cfg.maxOfficers });
      }
      await this.commit([
        putOp(C.clanPlayers, target, { ...t, role } satisfies ClanPlayerRecord),
        putOp(C.clans, String(clanId), this.bump(rec, now, { officerCount: rec.officerCount + (role === 'officer' ? 1 : -1) })),
        await this.historyOp(clanId, [{ type: 'role', actorId: playerId, targetId: target, role }], now),
      ]);
      this.sendMembership(target, clanId, role, 'role');
      await this.sendUpdated(clanId);
      return {};
    });
  }

  /**
   * The writes that make `next` the leader instead of `old` (both members of `rec`): the old leader becomes an officer
   * while there is room (`next`'s own officer place counts), else a member.
   */
  private handOver(rec: ClanRecord, old: ClanPlayerRecord | null, next: ClanPlayerRecord, now: number): { ops: StorageBatchOp[]; oldRole: ClanRole | null } {
    const officers = rec.officerCount - (next.role === 'officer' ? 1 : 0);
    const oldRole: ClanRole | null = old ? (officers < this.cfg.maxOfficers ? 'officer' : 'member') : null;
    const ops: StorageBatchOp[] = [putOp(C.clanPlayers, next.playerId, { ...next, role: 'leader' } satisfies ClanPlayerRecord)];
    if (old) ops.push(putOp(C.clanPlayers, old.playerId, { ...old, role: oldRole } satisfies ClanPlayerRecord));
    ops.push(putOp(C.clans, String(rec.clanId), this.bump(rec, now, { leaderId: next.playerId, officerCount: officers + (oldRole === 'officer' ? 1 : 0) })));
    return { ops, oldRole };
  }

  /** clan.transfer (clan:c). */
  async transfer(playerId: PlayerId, clanId: number, target: PlayerId): Promise<Record<string, never>> {
    if (target === playerId) throw new BackendError('VALIDATION_FAILED', 'You are the leader already', { reason: 'cannot_target_self' });
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      const leader = await this.actor(playerId, clanId, 'leader');
      const t = await this.readPlayer(target);
      if (t.clanId !== clanId) throw new BackendError('NOT_FOUND', 'The player is not a member of this clan', { reason: 'not_a_member', playerId: target });
      const { ops, oldRole } = this.handOver(rec, leader, t, now);
      ops.push(await this.historyOp(clanId, [{ type: 'leader', actorId: playerId, targetId: target }], now));
      await this.commit(ops);
      this.sendMembership(target, clanId, 'leader', 'leader');
      this.sendMembership(playerId, clanId, oldRole, 'role');
      await this.sendUpdated(clanId);
      return {};
    });
  }

  /** clan.leave (clanp:me -> clan:c); the only member leaving disbands the clan. */
  async leave(playerId: PlayerId): Promise<{ clanId: null; previousClanId: number | null; disbanded: boolean }> {
    await this.directory.ready();
    // The clan is read before the locks and checked under them: a change in between (a join or a kick under way)
    // starts over with the new clan.
    for (let attempt = 0; attempt < 100; attempt++) {
      const clanId = this.directory.clanIdOf(playerId);
      const keys = [clanPlayerLockKey(playerId), ...(clanId !== null ? [clanLockKey(clanId)] : [])];
      const out = await this.locked(keys, async () => {
        const now = this.d.clock.now();
        const me = await this.readPlayer(playerId);
        if (me.clanId !== clanId) return null;
        if (clanId === null) return { clanId: null, previousClanId: null, disbanded: false };
        const rec = await this.requireClan(clanId);
        if (me.role === 'leader') {
          if (rec.memberCount > 1) throw new BackendError('INVALID_STATE', 'Hand the leadership over first (clan.transfer)', { reason: 'leader_must_transfer' });
          await this.disbandLocked(rec);
          return { clanId: null, previousClanId: clanId, disbanded: true };
        }
        const zones = await this.zonesOf(playerId);
        await this.commit([
          putOp(C.clanPlayers, playerId, this.leftRecord(me, now, true)),
          putOp(C.clans, String(clanId), this.bump(rec, now, { memberCount: rec.memberCount - 1, officerCount: rec.officerCount - (me.role === 'officer' ? 1 : 0) })),
          await this.historyOp(clanId, [{ type: 'left', actorId: playerId, targetId: null, zones }], now),
        ]);
        this.sendMembership(playerId, null, null, 'left');
        await this.sendUpdated(clanId);
        return { clanId: null, previousClanId: clanId, disbanded: false } as const;
      });
      if (out) return out;
    }
    throw new BackendError('INTERNAL', 'The clan kept changing: try again', { reason: 'internal_error' });
  }

  /** clan.disband (clan:c). */
  async disband(playerId: PlayerId, clanId: number, confirmName: string): Promise<Record<string, never>> {
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const rec = await this.requireClan(clanId);
      await this.actor(playerId, clanId, 'leader');
      if (clanNameKey(confirmName) !== rec.nameKey) throw new BackendError('VALIDATION_FAILED', "confirmName is not the clan's name", { reason: 'disband_confirm_mismatch', field: 'confirmName' });
      await this.disbandLocked(rec);
      return {};
    });
  }

  /**
   * Disbands a clan (the caller holds clan:<c>): every member leaves (without the rejoin cooldown), the clan, its name,
   * history and requests are deleted in one batch. Returns the former members.
   */
  private async disbandLocked(rec: ClanRecord): Promise<PlayerId[]> {
    const members = this.directory.membersOf(rec.clanId);
    const ops: StorageBatchOp[] = [];
    for (const m of members) ops.push(putOp(C.clanPlayers, m, this.leftRecord(await this.readPlayer(m), 0, false)));
    ops.push(deleteOp(C.clans, String(rec.clanId)), deleteOp(C.clanNames, rec.nameKey), deleteOp(C.clanHistory, String(rec.clanId)));
    const requesters = this.directory.requestsOfClan(rec.clanId).map((r) => r.playerId);
    ops.push(...requesters.map((r) => deleteOp(C.clanRequests, clanRequestId(rec.clanId, r))));
    await this.commit(ops);
    this.d.logger.info('clan disbanded', { clanId: rec.clanId, members: members.length });
    for (const m of members) this.sendMembership(m, null, null, 'disbanded');
    // The players who asked to join: their requests ended (a player with a request is in no clan).
    for (const r of requesters) this.sendMembership(r, null, null, 'declined');
    return members;
  }

  // ------------------------------------------------------------------------------------------------ captures

  /**
   * A stored zone capture (AssaultService.onCapture; the zone is still locked): 'zone_captured' in the capturer's
   * clan history and 'zone_lost' in the former owner's one, each under its own clan lock, one after the other.
   */
  async recordCapture(n: CaptureNotice): Promise<void> {
    await this.directory.ready();
    // The capturer's count grew (its clan's rating with it): read it from the account the capture just stored. Marked
    // first, so that a failed read leaves the count to the next reader of ratings (settleCaptures), not stale for good,
    // and the history below is still written.
    const mark = this.directory.capturesChanged(n.playerId);
    try {
      const account = await this.auth.getAccount(n.playerId);
      if (account) this.directory.noteCaptures(n.playerId, account.captures, mark);
    } catch (err) {
      this.d.logger.warn('capture count not read: the next clan list reads it', { playerId: n.playerId, err });
    }
    const actorClan = this.directory.clanIdOf(n.playerId);
    const loserClan = n.previousOwnerId !== null && n.previousOwnerId !== n.playerId ? this.directory.clanIdOf(n.previousOwnerId) : null;
    if (actorClan !== null) await this.appendHistory(actorClan, [{ type: 'zone_captured', actorId: n.playerId, targetId: null, zone: n.zone, stage: n.stage }], n.at);
    if (loserClan !== null && loserClan !== actorClan) {
      await this.appendHistory(
        loserClan,
        [{ type: 'zone_lost', actorId: null, targetId: n.previousOwnerId, zone: n.zone, stage: n.stage, byId: n.playerId, byClanId: actorClan, byClanName: this.directory.brief(actorClan)?.name ?? null }],
        n.at,
      );
    }
  }

  private appendHistory(clanId: number, events: NewEvent[], at: number): Promise<void> {
    return this.locked([clanLockKey(clanId)], async () => {
      if (!(await this.readClan(clanId))) return;
      await this.commit([await this.historyOp(clanId, events, at)]);
    });
  }

  // ------------------------------------------------------------------------------------------------ operators

  /** A clan by an operator's reference: its id, or its name (compared like clan names); NOT_FOUND 'unknown_clan'. */
  async resolve(ref: string | number): Promise<ClanRecord> {
    await this.directory.ready();
    const text = String(ref).trim();
    let rec: ClanRecord | null = null;
    if (/^\d{1,10}$/.test(text)) rec = this.directory.clan(Number(text));
    const key = clanNameKey(text);
    if (!rec && key !== '' && key.length <= 200) {
      const named = await this.d.storage.get<ClanNameRecord>(C.clanNames, key);
      if (named) rec = this.directory.clan(named.clanId);
    }
    if (!rec) throw new BackendError('NOT_FOUND', `No clan "${text.slice(0, 64)}" (clan id or name)`, { reason: 'unknown_clan' });
    return rec;
  }

  /** Operator: renames the clan (the name rules without the leader's cooldown); history 'moderated' { action: 'rename' }. */
  async renameByOperator(clanId: number, rawName: string): Promise<{ previous: string; clan: ClanInfo }> {
    const name = this.checkName(rawName, false);
    await this.directory.ready();
    return this.locked([clanLockKey(clanId), clanNameLockKey(name.key)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      if (name.name === rec.name) return { previous: rec.name, clan: await this.infoOf(rec) };
      const ops: StorageBatchOp[] = [];
      if (name.key !== rec.nameKey) {
        if (await this.d.storage.get(C.clanNames, name.key)) throw new BackendError('CONFLICT', 'Another clan has this name', { reason: 'clan_name_taken', field: 'name' });
        ops.push(deleteOp(C.clanNames, rec.nameKey));
      }
      ops.push(
        putOp(C.clanNames, name.key, { clanId, name: name.name, key: name.key, at: now } satisfies ClanNameRecord),
        putOp(C.clans, String(clanId), this.bump(rec, now, { name: name.name, nameKey: name.key })),
        await this.historyOp(clanId, [{ type: 'moderated', actorId: null, targetId: null, action: 'rename', from: rec.name, to: name.name }], now),
      );
      await this.commit(ops);
      this.d.logger.warn('clan renamed by an operator', { clanId });
      await this.sendUpdated(clanId);
      return { previous: rec.name, clan: await this.infoOf(this.directory.clan(clanId)!) };
    });
  }

  /** Operator: removes the description; history 'moderated' { action: 'description' }. */
  async clearDescriptionByOperator(clanId: number): Promise<ClanInfo> {
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      if (rec.description === '') return this.infoOf(rec);
      await this.commit([
        putOp(C.clans, String(clanId), this.bump(rec, now, { description: '' })),
        await this.historyOp(clanId, [{ type: 'moderated', actorId: null, targetId: null, action: 'description' }], now),
      ]);
      await this.sendUpdated(clanId);
      return this.infoOf(this.directory.clan(clanId)!);
    });
  }

  /** The member who takes over from a leader that goes: the officer who joined first, else the member who did. */
  private async successorOf(clanId: number, leaving: PlayerId): Promise<ClanPlayerRecord | null> {
    const candidates: ClanPlayerRecord[] = [];
    for (const m of this.directory.membersOf(clanId)) if (m !== leaving) candidates.push(await this.readPlayer(m));
    candidates.sort((a, b) => (a.role === 'officer' ? 0 : 1) - (b.role === 'officer' ? 0 : 1) || (a.joinedAt ?? 0) - (b.joinedAt ?? 0) || (a.playerId < b.playerId ? -1 : 1));
    return candidates[0] ?? null;
  }

  /**
   * Operator: takes a member out of the clan (with the rejoin cooldown); a leader is replaced by its successor
   * (successorOf), the last member's removal disbands the clan. History 'moderated' { action: 'remove', target }.
   */
  async removeMemberByOperator(clanId: number, playerId: PlayerId): Promise<{ playerId: PlayerId; clanId: number; disbanded: boolean; newLeaderId: PlayerId | null }> {
    await this.directory.ready();
    return this.locked([clanPlayerLockKey(playerId), clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      const t = await this.readPlayer(playerId);
      if (t.clanId !== clanId) throw new BackendError('NOT_FOUND', 'The player is not a member of this clan', { reason: 'not_a_member', playerId });
      if (rec.memberCount <= 1) {
        await this.disbandLocked(rec);
        return { playerId, clanId, disbanded: true, newLeaderId: null };
      }
      let after = this.bump(rec, now, { memberCount: rec.memberCount - 1, officerCount: rec.officerCount - (t.role === 'officer' ? 1 : 0) });
      const ops: StorageBatchOp[] = [putOp(C.clanPlayers, playerId, this.leftRecord(t, now, true))];
      const events: NewEvent[] = [{ type: 'moderated', actorId: null, targetId: playerId, action: 'remove' }];
      let successor: ClanPlayerRecord | null = null;
      if (t.role === 'leader') {
        successor = await this.successorOf(clanId, playerId);
        if (successor) {
          after = { ...after, leaderId: successor.playerId, officerCount: after.officerCount - (successor.role === 'officer' ? 1 : 0) };
          ops.push(putOp(C.clanPlayers, successor.playerId, { ...successor, role: 'leader' } satisfies ClanPlayerRecord));
          events.push({ type: 'leader_auto', actorId: null, targetId: successor.playerId, reason: 'moderated' });
        }
      }
      ops.push(putOp(C.clans, String(clanId), after), await this.historyOp(clanId, events, now));
      await this.commit(ops);
      this.d.logger.warn('clan member removed by an operator', { clanId, playerId });
      this.sendMembership(playerId, null, null, 'kicked');
      if (successor) this.sendMembership(successor.playerId, clanId, 'leader', 'leader');
      await this.sendUpdated(clanId);
      return { playerId, clanId, disbanded: false, newLeaderId: successor?.playerId ?? null };
    });
  }

  /** Operator: makes a member the leader (the old leader becomes an officer, or a member without room); history 'moderated'. */
  async setLeaderByOperator(clanId: number, playerId: PlayerId): Promise<{ clanId: number; leaderId: PlayerId; previousLeaderId: PlayerId }> {
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const now = this.d.clock.now();
      const rec = await this.requireClan(clanId);
      const t = await this.readPlayer(playerId);
      if (t.clanId !== clanId) throw new BackendError('NOT_FOUND', 'The player is not a member of this clan', { reason: 'not_a_member', playerId });
      if (rec.leaderId === playerId) return { clanId, leaderId: playerId, previousLeaderId: playerId };
      const old = await this.readPlayer(rec.leaderId);
      const { ops, oldRole } = this.handOver(rec, old.clanId === clanId ? old : null, t, now);
      ops.push(await this.historyOp(clanId, [{ type: 'moderated', actorId: null, targetId: playerId, action: 'leader' }], now));
      await this.commit(ops);
      this.d.logger.warn('clan leader set by an operator', { clanId, playerId });
      this.sendMembership(playerId, clanId, 'leader', 'leader');
      if (old.clanId === clanId) this.sendMembership(old.playerId, clanId, oldRole, 'role');
      await this.sendUpdated(clanId);
      return { clanId, leaderId: playerId, previousLeaderId: rec.leaderId };
    });
  }

  /** Operator: disbands the clan (as its leader's clan.disband). */
  async disbandByOperator(clanId: number): Promise<{ clanId: number; name: string; members: number }> {
    await this.directory.ready();
    return this.locked([clanLockKey(clanId)], async () => {
      const rec = await this.requireClan(clanId);
      const members = await this.disbandLocked(rec);
      this.d.logger.warn('clan disbanded by an operator', { clanId });
      return { clanId, name: rec.name, members: members.length };
    });
  }

  /** Every clan as the operator console lists it: the summary, the leader, the officers, the creation time. */
  async operatorRows(): Promise<(ClanSummary & { leaderId: PlayerId; leaderName: string; officerCount: number; createdAt: number; description: string })[]> {
    await this.directory.ready();
    const names = await this.names(this.directory.allClans().map((r) => r.leaderId));
    const owners = await this.zoneOwners();
    await this.directory.settleCaptures();
    const zoneCounts = this.zoneCounts(owners);
    return this.directory.allClans().map((rec) => ({
      ...this.summaryOf(rec, zoneCounts.get(rec.clanId) ?? 0, this.directory.ratingOf(rec.clanId)),
      leaderId: rec.leaderId,
      leaderName: names.get(rec.leaderId) ?? DELETED_PLAYER_NAME,
      officerCount: rec.officerCount,
      createdAt: rec.createdAt,
      description: rec.description,
    }));
  }

  /** The pending requests of a clan as views (the operator console). */
  async requestsOf(clanId: number): Promise<ClanJoinRequest[]> {
    await this.directory.ready();
    const out: ClanJoinRequest[] = [];
    for (const r of this.directory.requestsOfClan(clanId)) out.push(await this.requestView(r));
    return out;
  }

  /** Checks the invariants of the header against the stored documents and the directory; returns the problems found. */
  async verify(): Promise<string[]> {
    await this.directory.ready();
    const problems: string[] = [];
    const s = this.d.storage;
    const clans = new Map((await s.list<ClanRecord>(C.clans)).map((c) => [c.clanId, c]));
    const players = await s.list<ClanPlayerRecord>(C.clanPlayers);
    const byClan = new Map<number, ClanPlayerRecord[]>();
    for (const p of players) {
      if (p.clanId === null) {
        if (p.role !== null) problems.push(`player ${p.playerId}: a role without a clan`);
        continue;
      }
      if (!clans.has(p.clanId)) problems.push(`player ${p.playerId}: in clan ${p.clanId}, which does not exist`);
      if (p.role === null) problems.push(`player ${p.playerId}: in clan ${p.clanId} without a role`);
      byClan.set(p.clanId, [...(byClan.get(p.clanId) ?? []), p]);
    }
    for (const c of clans.values()) {
      const members = byClan.get(c.clanId) ?? [];
      const leaders = members.filter((m) => m.role === 'leader');
      const officers = members.filter((m) => m.role === 'officer').length;
      if (members.length === 0) problems.push(`clan ${c.clanId}: no members`);
      if (members.length !== c.memberCount) problems.push(`clan ${c.clanId}: memberCount ${c.memberCount}, members ${members.length}`);
      if (officers !== c.officerCount) problems.push(`clan ${c.clanId}: officerCount ${c.officerCount}, officers ${officers}`);
      if (leaders.length !== 1 || leaders[0].playerId !== c.leaderId) problems.push(`clan ${c.clanId}: leaderId ${c.leaderId}, leaders ${leaders.map((l) => l.playerId).join(', ') || 'none'}`);
      if (c.officerCount > this.cfg.maxOfficers) problems.push(`clan ${c.clanId}: ${c.officerCount} officers, at most ${this.cfg.maxOfficers}`);
      if (c.memberCount > this.cfg.maxMembers) problems.push(`clan ${c.clanId}: ${c.memberCount} members, at most ${this.cfg.maxMembers}`);
      const name = await s.get<ClanNameRecord>(C.clanNames, c.nameKey);
      if (!name || name.clanId !== c.clanId) problems.push(`clan ${c.clanId}: no name index ${c.nameKey}`);
      if (clanNameKey(c.name) !== c.nameKey) problems.push(`clan ${c.clanId}: nameKey ${c.nameKey} is not the key of "${c.name}"`);
      const dirMembers = new Set(this.directory.membersOf(c.clanId));
      if (dirMembers.size !== members.length || members.some((m) => !dirMembers.has(m.playerId))) problems.push(`clan ${c.clanId}: the directory's members differ from the stored ones`);
      let captures = 0;
      for (const m of members) captures += Number((await this.auth.getAccount(m.playerId))?.captures) || 0;
      await this.directory.settleCaptures();
      const rating = this.directory.ratingOf(c.clanId);
      if (rating !== captures) problems.push(`clan ${c.clanId}: the directory's rating ${rating}, its members' captures ${captures}`);
    }
    for (const n of await s.list<ClanNameRecord>(C.clanNames)) {
      const c = clans.get(n.clanId);
      if (!c || c.nameKey !== n.key) problems.push(`name index ${n.key}: no clan ${n.clanId} with that name`);
    }
    const playerClan = new Map(players.map((p) => [p.playerId, p.clanId]));
    for (const r of await s.list<ClanRequestRecord>(C.clanRequests)) {
      if (!clans.has(r.clanId)) problems.push(`request ${r.clanId}~${r.playerId}: the clan does not exist`);
      if ((playerClan.get(r.playerId) ?? null) !== null) problems.push(`request ${r.clanId}~${r.playerId}: the player is in a clan`);
    }
    const accounts = new Set((await this.auth.listAccounts()).map((a) => a.playerId));
    for (const h of await s.list<ClanHistoryRecord>(C.clanHistory)) {
      if (!clans.has(h.clanId)) problems.push(`history ${h.clanId}: the clan does not exist`);
      if (h.events.length > this.cfg.historyKept) problems.push(`history ${h.clanId}: ${h.events.length} events, at most ${this.cfg.historyKept}`);
      const named = new Set(h.events.flatMap((e) => [e.actorId, e.targetId, e.byId ?? null]));
      const unknown = [...named].filter((id): id is PlayerId => id !== null && id !== DELETED_PLAYER_ID && !accounts.has(id)).sort();
      if (unknown.length > 0) problems.push(`history ${h.clanId}: names ${unknown.join(', ')}, which no account has (an account deletion that did not finish: the server finishes it on its next sweep or start)`);
    }
    return problems;
  }

  // ------------------------------------------------------------------------------------------------ account deletion

  /**
   * The locks the account deletion takes for the clans (in the lock order, after the auction lock): the player's
   * clanp lock and, when it is in a clan, that clan's lock. The clan is checked again under them (deletionOps).
   */
  async deletionLocks(playerId: PlayerId): Promise<{ keys: string[]; clanId: number | null }> {
    await this.directory.ready();
    const clanId = this.directory.clanIdOf(playerId);
    return { keys: [clanPlayerLockKey(playerId), ...(clanId !== null ? [clanLockKey(clanId)] : [])], clanId };
  }

  /**
   * Account deletion, under deletionLocks: the writes that take the player out of the clans. Its record and its
   * requests go; in its clan the counts drop, a leader is replaced by its successor (successorOf; 'leader_auto'
   * { reason: 'account_deleted' }), 'member_deleted' is recorded and the clan's history names it as DELETED_PLAYER_ID;
   * a clan it was the only member of is disbanded. Null when its clan is no longer `clanId` (take the locks again).
   */
  async deletionOps(playerId: PlayerId, clanId: number | null, at: number): Promise<ClanDeletion | null> {
    const stored = await this.d.storage.get<ClanPlayerRecord>(C.clanPlayers, playerId);
    if ((stored?.clanId ?? null) !== clanId) return null;
    const ops: StorageBatchOp[] = [];
    if (stored) ops.push(deleteOp(C.clanPlayers, playerId));
    const requests = this.directory.requestsOfPlayer(playerId);
    ops.push(...requests.map((r) => deleteOp(C.clanRequests, clanRequestId(r.clanId, playerId))));
    const out: ClanDeletion = { playerId, ops, clanId, disbanded: false, successor: null, requestClans: requests.map((r) => r.clanId) };
    const rec = clanId !== null ? await this.readClan(clanId) : null;
    if (!rec || !stored) return out;
    if (rec.memberCount <= 1) {
      ops.push(deleteOp(C.clans, String(rec.clanId)), deleteOp(C.clanNames, rec.nameKey), deleteOp(C.clanHistory, String(rec.clanId)));
      ops.push(...this.directory.requestsOfClan(rec.clanId).map((r) => deleteOp(C.clanRequests, clanRequestId(r.clanId, r.playerId))));
      out.disbanded = true;
      return out;
    }
    let after = this.bump(rec, at, { memberCount: rec.memberCount - 1, officerCount: rec.officerCount - (stored.role === 'officer' ? 1 : 0) });
    const events: NewEvent[] = [{ type: 'member_deleted', actorId: null, targetId: DELETED_PLAYER_ID }];
    if (stored.role === 'leader') {
      const successor = await this.successorOf(rec.clanId, playerId);
      if (successor) {
        after = { ...after, leaderId: successor.playerId, officerCount: after.officerCount - (successor.role === 'officer' ? 1 : 0) };
        ops.push(putOp(C.clanPlayers, successor.playerId, { ...successor, role: 'leader' } satisfies ClanPlayerRecord));
        events.push({ type: 'leader_auto', actorId: null, targetId: successor.playerId, reason: 'account_deleted' });
        out.successor = successor.playerId;
      }
    }
    const history = await this.d.storage.get<ClanHistoryRecord>(C.clanHistory, String(rec.clanId));
    ops.push(putOp(C.clans, String(rec.clanId), after), await this.historyOp(rec.clanId, events, at, history ? withoutPlayerId(history, playerId) : undefined));
    return out;
  }

  /**
   * After the deletion's batch, still under its locks: the directory learns of it (and forgets the player's capture
   * count); the zones the batch released count for no clan from now on.
   */
  deletionStored(d: ClanDeletion): void {
    this.directory.apply(d.ops);
    this.directory.forgetCaptures(d.playerId);
    this.zonesChanged();
  }

  /** After the deletion's locks: the events (the new leader, the clan's members, the staff of clans that lost a request). */
  async deletionEvents(d: ClanDeletion): Promise<void> {
    if (d.successor && d.clanId !== null) this.sendMembership(d.successor, d.clanId, 'leader', 'leader');
    if (d.clanId !== null && !d.disbanded) await this.sendUpdated(d.clanId);
    this.sendRequests(d.requestClans);
  }

  /**
   * After the deletion's batch (and again whenever a deletion is finished later: idempotent): the player's id leaves
   * every clan history that still names it (the other clans'; its own clan's changed in the batch), each under its
   * clan lock. Returns the number of histories changed.
   */
  async deletionHistories(playerId: PlayerId): Promise<number> {
    let changed = 0;
    for (const h of await this.d.storage.list<ClanHistoryRecord>(C.clanHistory)) {
      if (!mentions(h, playerId)) continue;
      await this.locked([clanLockKey(h.clanId)], async () => {
        const current = await this.d.storage.get<ClanHistoryRecord>(C.clanHistory, String(h.clanId));
        if (!current || !mentions(current, playerId)) return;
        await this.d.storage.batch([putOp(C.clanHistory, String(h.clanId), withoutPlayerId(current, playerId))]);
        changed++;
      });
    }
    return changed;
  }
}
